import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DependentView,
  InsuranceStatus,
  BulkUpdateInsuranceInput,
  bulkUpdateInsurance,
} from '../../../api/General/dependents';
import {
  Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter,
  DialogTitle,
} from '../../../components/ui/Dialog';
import Button from '../../../components/ui/Button';

interface BulkUpdateInsuranceModalProps {
  dependents: DependentView[];
  open: boolean;
  onClose: () => void;
  onUpdated: () => void;
}

const insuranceStatusOptions = [
  { value: InsuranceStatus.Pending, labelKey: 'insurancePending' },
  { value: InsuranceStatus.Active, labelKey: 'insuranceActive' },
  { value: InsuranceStatus.Inactive, labelKey: 'insuranceInactive' },
];

export default function BulkUpdateInsuranceModal({ dependents, open, onClose, onUpdated }: BulkUpdateInsuranceModalProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<InsuranceStatus>(InsuranceStatus.Active);
  const [provider, setProvider] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const input: BulkUpdateInsuranceInput = {
        dependentIds: dependents.map(d => d.id),
        insuranceStatus: status,
        insuranceProvider: provider || undefined,
        insurancePolicyNumber: policyNumber || undefined,
        insuranceExpiryDate: expiryDate || undefined,
      };

      const result = await bulkUpdateInsurance(input);
      if (result.errors.length === 0) {
        onUpdated();
        onClose();
      } else {
        setError(result.errors[0] || t('dependents.insurance.updateError'));
      }
    } catch {
      setError(t('dependents.insurance.updateError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open && dependents.length > 0} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{t('dependents.insurance.bulkTitle')}</DialogTitle>
        </DialogHeader>

        <DialogBody>
          {/* Selection Summary */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span className="text-sm font-medium text-primary">
                {t('dependents.insurance.selectedCount', { count: dependents.length })}
              </span>
            </div>
            <div className="max-h-24 overflow-y-auto space-y-1">
              {dependents.map((dep) => (
                <div key={dep.id} className="flex items-center justify-between text-sm text-primary">
                  <span>{dep.fullName}</span>
                  <span className="text-xs text-primary">({dep.employeeName})</span>
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" id="bulk-insurance-form">
            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('dependents.insuranceStatus')}
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as InsuranceStatus)}
                className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                disabled={saving}
              >
                {insuranceStatusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {t(`dependents.insurance${opt.labelKey}`)}
                  </option>
                ))}
              </select>
            </div>

            {/* Provider */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('dependents.provider')}
              </label>
              <input
                type="text"
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                placeholder={t('dependents.insurance.providerPlaceholder')}
                className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                disabled={saving}
              />
              <p className="mt-1 text-xs text-muted-foreground">{t('dependents.insurance.leaveEmptyToKeep')}</p>
            </div>

            {/* Policy Number */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('dependents.policyNumber')}
              </label>
              <input
                type="text"
                value={policyNumber}
                onChange={(e) => setPolicyNumber(e.target.value)}
                placeholder={t('dependents.insurance.policyPlaceholder')}
                className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                disabled={saving}
              />
            </div>

            {/* Expiry Date */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('dependents.expiryDate')}
              </label>
              <input
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                disabled={saving}
              />
            </div>

            {/* Preview Toggle */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="bulkPreview"
                checked={showPreview}
                onChange={(e) => setShowPreview(e.target.checked)}
                className="rounded border-border text-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
              <label htmlFor="bulkPreview" className="text-sm text-foreground">
                {t('dependents.insurance.previewChanges')}
              </label>
            </div>

            {/* Preview */}
            {showPreview && (
              <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
                <h4 className="text-sm font-medium text-warning mb-2">{t('dependents.insurance.preview')}</h4>
                <div className="text-sm text-warning">
                  <p className="mb-2">
                    {t('dependents.insurance.previewDescription', { count: dependents.length })}
                  </p>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>{t('dependents.insuranceStatus')}: {t(`dependents.insurance${insuranceStatusOptions.find(o => o.value === status)?.labelKey}`)}</li>
                    {provider && <li>{t('dependents.provider')}: {provider}</li>}
                    {policyNumber && <li>{t('dependents.policyNumber')}: {policyNumber}</li>}
                    {expiryDate && <li>{t('dependents.expiryDate')}: {expiryDate}</li>}
                  </ul>
                </div>
              </div>
            )}

            {/* Warning */}
            <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-warning mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <h4 className="text-sm font-medium text-warning mb-1">{t('dependents.insurance.bulkWarningTitle')}</h4>
                  <p className="text-sm text-warning">
                    {t('dependents.insurance.bulkWarning', { count: dependents.length })}
                  </p>
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-3 text-sm text-destructive">
                {error}
              </div>
            )}
          </form>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form="bulk-insurance-form" loading={saving}>
            {t('dependents.insurance.updateCount', { count: dependents.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
